/*
  <search-drawer>: the right-side search panel (snippets/header-search.liquid,
  styles in design-system-overrides.css). One instance at <body> level holds
  the page's single predictive-search form; header icons
  ([data-search-drawer-trigger], real links to /search without JS) open it.

  Opening is synchronous on purpose: the drawer becomes visible and the input
  takes focus inside the click's own event, because iOS only raises the
  keyboard for focus that happens in the tap itself.
*/
class SearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.panel = this.querySelector('.search-drawer__panel');
    this.input = this.querySelector('input[type="search"]');
    this.trigger = null;

    this.querySelector('[data-search-drawer-overlay]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-search-drawer-close]')?.addEventListener('click', () => this.close());
    this.addEventListener('keydown', this.onKeydown.bind(this));

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-search-drawer-trigger]');
      if (!trigger) return;
      // Let modified clicks (new tab etc.) follow the /search link.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(trigger);
    });

    document.addEventListener('keydown', (event) => {
      const trigger = event.target.closest?.('[data-search-drawer-trigger]');
      if (!trigger || event.code !== 'Space') return;
      event.preventDefault();
      this.open(trigger);
    });

    // Focus must not escape the open dialog (the page behind is dimmed).
    document.addEventListener('focusin', (event) => {
      if (this.isOpen && !this.contains(event.target)) this.input?.focus({ preventScroll: true });
    });

    // Coming back from the back/forward cache with the drawer still open would
    // leave the page scroll-locked.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.close(false);
    });
  }

  get isOpen() {
    return this.classList.contains('active');
  }

  open(trigger) {
    if (this.isOpen) return;

    // One panel at a time: both drawers lock scroll with the same body class.
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.classList.contains('active')) cartDrawer.close();

    this.trigger = trigger || null;
    this.trigger?.setAttribute('aria-expanded', 'true');

    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    this.input?.focus({ preventScroll: true });
  }

  close(restoreFocus = true) {
    if (!this.isOpen) return;

    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    this.input?.blur();
    this.trigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.trigger?.focus();
  }

  onKeydown(event) {
    if (event.code === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.code === 'Tab') this.trapTab(event);
  }

  // Recomputed on every Tab: the results panel is replaced by each query, so a
  // first/last pair captured once at open would go stale.
  trapTab(event) {
    const focusable = getFocusableElements(this.panel).filter(
      (element) => element.offsetParent !== null || element === document.activeElement
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !this.panel.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !this.panel.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('search-drawer', SearchDrawer);
